import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setConnectModal } from "../../ReduxSlices/ModalSlice";
import { setDisconnect } from "../../ReduxSlices/AuthSlice";
import { truncateWalletAddress } from "../functions/Functions";
import { getNibiruSDK } from "../../config/network";
import { getLastMarketPath } from "../../utils/lastMarket";
import DisconnectModal from "../Modal/DisconnectModal";
import RefCodesModal from "../Modal/RefCodesModal";
import BridgeModal from "../Bridge/BridgeModal";
import { PokeBall } from "./Objects3D";
import { useBall, cycleBall } from "./ballThemes";
import { PointsWidget } from "../PointsWidget";
import { Logo } from "../../AssetComponents/Images";
import { hapticTap, hapticSelect } from "./haptics";
import "./cockpit.css";

const nibiru = getNibiruSDK();

const keplrIcon = "/assets/images/Keplr_icon_ver.1.3_2.svg";
const metamaskIcon = "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg";

/** Wallet brand icon for the disconnect card (mirrors Header's mapping). */
const getWalletIcon = (walletType) => {
  switch (walletType) {
    case "keplr":
      return keplrIcon;
    case "metamask":
    case "MetaMask":
      return metamaskIcon;
    case "thirdweb":
      return "https://thirdweb.com/favicon.ico";
    default:
      return "";
  }
};

/**
 * Pokédex device header — the two collectible card routes only.
 *
 * The app's standard `Header` is hidden on these routes (see `MainLayouts`),
 * so this carries the same responsibilities — routing, wallet connect and
 * disconnect, referral codes, bridge, and the points chip. Every modal is the
 * app's existing one, so behaviour is unchanged; only the chrome differs.
 *
 * The lens, the three LEDs and the white wordmark are the spec's "header
 * furniture" — what makes the bar read as the device rather than a nav.
 * Carries `ce-raw` so the global theme sweeps leave its parts alone.
 */
export function CockpitHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isConnected = useSelector((state) => state.auth.isConnected);
  const account = useSelector((state) => state.auth.account);
  const wallet = useSelector((state) => state.auth.wallet);
  const ethereum = useSelector((state) => state.auth.ethereum);
  const bech32Address = useSelector((state) => state.auth.bech32Address);

  const ball = useBall();

  const [showDisconnect, setShowDisconnect] = useState(false);
  const [showRefCodes, setShowRefCodes] = useState(false);
  const [bridgeOpen, setBridgeOpen] = useState(false);

  // Below md the controls collapse into a drawer, matching how the app's
  // standard Header behaves on small screens — six wrapped pills filled most
  // of the viewport otherwise.
  const [isNarrow, setIsNarrow] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  useEffect(() => {
    if (!isNarrow) setMenuOpen(false);
  }, [isNarrow]);

  /** Mirrors Header's disconnect: tear down the wallet session, then clear auth. */
  const handleDisconnect = async () => {
    try {
      if (wallet === "keplr" && window.keplr && nibiru?.chainId) {
        await window.keplr.disable(nibiru.chainId);
      }
    } catch (error) {
      console.error("Error disconnecting wallet:", error);
    } finally {
      dispatch(setDisconnect());
      setShowDisconnect(false);
    }
  };

  const items = [
    { label: "Markets", path: getLastMarketPath(), match: "/perps/markets" },
    { label: "Liquidity", path: "/perps/vaults", match: "/perps/vaults" },
    { label: "Portfolio", path: "/perps/trades", match: "/perps/trades" },
  ];

  return (
    <div
      className="ce-raw"
      style={isNarrow ? { ...styles.bar, ...styles.barNarrow } : styles.bar}
    >
      {/* ── Device furniture: lens + LEDs + wordmark ── */}
      <div style={isNarrow ? { ...styles.brand, ...styles.brandNarrow } : styles.brand}>
        <div style={isNarrow ? { ...styles.lens, ...styles.lensNarrow } : styles.lens} aria-hidden="true">
          <div className="ce-hdr-lens" style={isNarrow ? { ...styles.lensInner, ...styles.lensInnerNarrow } : styles.lensInner}>
            <span className="ce-hdr-glint" style={styles.lensGlint} />
          </div>
        </div>
        <div style={isNarrow ? { ...styles.leds, gap: 4 } : styles.leds} aria-hidden="true">
          <span className="ce-hdr-led" style={{ ...styles.led, ...(isNarrow ? styles.ledNarrow : null), background: "var(--pd-accent)" }} />
          <span className="ce-hdr-led" style={{ ...styles.led, ...(isNarrow ? styles.ledNarrow : null), background: "var(--pd-led-yellow)" }} />
          <span className="ce-hdr-led" style={{ ...styles.led, ...(isNarrow ? styles.ledNarrow : null), background: "var(--pd-led-green)" }} />
        </div>
        {/* The brand mark sits on a moulded nameplate: its wordmark is #323260,
            which would all but vanish against the red shell. A panel face with
            the standard black outline gives it contrast and makes it read as a
            part of the device rather than text floating on the casing. */}
        <button
          onClick={() => { hapticTap(); navigate(getLastMarketPath()); }}
          style={isNarrow ? { ...styles.nameplate, ...styles.nameplateNarrow } : styles.nameplate}
          aria-label="Coded Estate — go to markets"
        >
          <Logo className="ce-brand-logo" />
        </button>
      </div>

      {/* ── Nav: control block, filled active tab ── */}
      {!isNarrow && (
      <div style={styles.controlBlock}>
        {items.map((it) => {
          const active = location.pathname.startsWith(it.match);
          return (
            <button
              key={it.label}
              onClick={() => { hapticSelect(); navigate(it.path); }}
              style={active ? { ...styles.tab, ...styles.tabActive } : styles.tab}
            >
              {it.label}
            </button>
          );
        })}
      </div>
      )}

      {/* ── Right cluster ── */}
      {!isNarrow && (
      <div style={styles.right}>
        {/* Ball loader — the same control the switcher carries, so the choice
            can be changed from either Pokédex screen. */}
        <button
          onClick={() => { hapticTap(); cycleBall(); }}
          className="ce-dex-ballbtn"
          style={{ width: 34, height: 34 }}
          aria-label={`Ball loader — ${ball.name}. Click to load the next ball.`}
          title={ball.name}
        >
          <PokeBall d={30} ball={ball} />
        </button>
        {/* The wrapper is a styling hook only — no box of its own. PointsWidget
            is shared, so cockpit.css reaches its pill through this class
            instead of the component being edited. */}
        {isConnected && (
          <div className="ce-points-chip">
            <PointsWidget />
          </div>
        )}
        {isConnected && (
          <>
            <button onClick={() => { hapticTap(); setShowRefCodes(true); }} style={styles.secondary}>
              Referral
            </button>
            <button onClick={() => { hapticTap(); setBridgeOpen(true); }} style={styles.secondary}>
              Bridge
            </button>
          </>
        )}
        {isConnected && account ? (
          <button onClick={() => { hapticTap(); setShowDisconnect(true); }} className="ce-num" style={styles.primary}>
            {truncateWalletAddress(account)}
          </button>
        ) : (
          <button onClick={() => { hapticTap(); dispatch(setConnectModal(true)); }} style={styles.primary}>
            Connect Wallet
          </button>
        )}
      </div>
      )}

      {/* ── Narrow: everything behind one control ── */}
      {isNarrow && (
        <button
          onClick={() => { hapticTap(); cycleBall(); }}
          className="ce-dex-ballbtn"
          style={{ width: 30, height: 30, marginLeft: "auto" }}
          aria-label={`Ball loader — ${ball.name}. Click to load the next ball.`}
          title={ball.name}
        >
          <PokeBall d={26} ball={ball} />
        </button>
      )}

      {isNarrow && (
        <button
          onClick={() => { hapticTap(); setMenuOpen((v) => !v); }}
          style={styles.burger}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          <span style={styles.burgerBar} />
          <span style={styles.burgerBar} />
          <span style={styles.burgerBar} />
        </button>
      )}

      {isNarrow && menuOpen && (
        <div style={styles.drawer}>
          {items.map((it) => {
            const active = location.pathname.startsWith(it.match);
            return (
              <button
                key={it.label}
                onClick={() => { hapticSelect(); setMenuOpen(false); navigate(it.path); }}
                style={active ? { ...styles.drawerItem, ...styles.drawerItemActive } : styles.drawerItem}
              >
                {it.label}
              </button>
            );
          })}

          <div style={styles.drawerRule} />

          {isConnected && (
            <div className="ce-points-chip" style={{ alignSelf: "flex-start" }}>
              <PointsWidget />
            </div>
          )}
          {isConnected && (
            <>
              <button onClick={() => { hapticTap(); setMenuOpen(false); setShowRefCodes(true); }} style={styles.drawerItem}>
                Referral
              </button>
              <button onClick={() => { hapticTap(); setMenuOpen(false); setBridgeOpen(true); }} style={styles.drawerItem}>
                Bridge
              </button>
            </>
          )}
          {isConnected && account ? (
            <button
              onClick={() => { hapticTap(); setMenuOpen(false); setShowDisconnect(true); }}
              className="ce-num"
              style={{ ...styles.primary, width: "100%" }}
            >
              {truncateWalletAddress(account)}
            </button>
          ) : (
            <button
              onClick={() => { hapticTap(); setMenuOpen(false); dispatch(setConnectModal(true)); }}
              style={{ ...styles.primary, width: "100%" }}
            >
              Connect Wallet
            </button>
          )}
        </div>
      )}

      {showDisconnect && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center"
          onClick={() => setShowDisconnect(false)}
        >
          <div className="absolute inset-0" style={{ background: "rgba(27,27,27,0.55)" }} />
          <div onClick={(e) => e.stopPropagation()} className="relative z-10">
            <DisconnectModal
              account={account}
              bech32Address={bech32Address}
              evmAddress={ethereum}
              walletIcon={getWalletIcon(wallet)}
              onDisconnect={handleDisconnect}
            />
          </div>
        </div>
      )}
      <RefCodesModal open={showRefCodes} onClose={() => setShowRefCodes(false)} />
      <BridgeModal
        open={bridgeOpen}
        onClose={() => setBridgeOpen(false)}
        walletAddress={ethereum || (account?.startsWith("0x") ? account : null)}
        initialTab="deposit"
        // Cosmos wallets can't sign the LayerZero flow (it uses window.ethereum),
        // so they get the redirect-only providers.
        cosmosOnly={wallet === "keplr" || wallet === "leap"}
      />
    </div>
  );
}

/* Spec §6 furniture + §3 button variants. Flat offsets only — no glow, no blur.
   The lens gradient is the single gradient the spec permits in the UI. */
const styles = {
  bar: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
    padding: "14px 22px",
    background: "transparent",
  },
  brand: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
  /* Narrow: one row, nothing wraps. The bar is the constraint, so the brand
     cluster shrinks rather than pushing the menu control onto a second line. */
  barNarrow: { flexWrap: "nowrap", gap: 8, padding: "10px 12px" },
  brandNarrow: { gap: 8, flexShrink: 1, minWidth: 0 },
  lensNarrow: { width: 34, height: 34, borderWidth: 2 },
  lensInnerNarrow: { width: 22, height: 22 },
  nameplateNarrow: { padding: "5px 8px", flexShrink: 1, minWidth: 0, overflow: "hidden" },
  lens: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    background: "var(--pd-panel)",
    border: "3px solid var(--pd-black)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  lensInner: {
    position: "relative",
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: "2px solid var(--pd-black)",
    // Tinted off the loaded ball's accent, matching the switcher's big lens.
    background:
      "radial-gradient(circle at 34% 30%, rgba(255,255,255,.88), rgba(255,255,255,.38) 46%, rgba(255,255,255,0) 78%)," +
      "radial-gradient(circle at 62% 82%, rgba(11,18,32,.35), rgba(11,18,32,0) 62%)," +
      "var(--pd-accent)",
  },
  lensGlint: {
    position: "absolute",
    top: 4,
    left: 5,
    width: 9,
    height: 7,
    borderRadius: "50%",
    background: "#ffffff",
  },
  leds: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 },
  ledNarrow: { width: 8, height: 8, borderWidth: 2 },
  led: {
    width: 13,
    height: 13,
    borderRadius: "50%",
    border: "2px solid var(--pd-black)",
    display: "block",
  },
  nameplate: {
    display: "flex",
    alignItems: "center",
    background: "var(--pd-panel)",
    border: "var(--pd-border)",
    borderRadius: "var(--pd-r-control)",
    boxShadow: "var(--pd-drop-sm)",
    padding: "7px 12px",
    cursor: "pointer",
  },
  controlBlock: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: 4,
    background: "var(--pd-panel)",
    border: "3px solid var(--pd-black)",
    borderRadius: 12,
    boxShadow: "var(--pd-drop-sm)",
    flexWrap: "wrap",
  },
  tab: {
    padding: "8px 14px",
    borderRadius: "var(--pd-r-control)",
    border: "2px solid transparent",
    background: "transparent",
    color: "var(--pd-ink-2)",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
    transition: "background-color .2s, color .2s",
  },
  tabActive: {
    background: "var(--pd-panel)",
    color: "var(--pd-ink)",
    border: "var(--pd-border-sm)",
    boxShadow: "var(--pd-drop-sm)",
    fontWeight: 800,
  },
  right: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  burger: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 4,
    width: 44,
    height: 40,
    padding: "0 10px",
    background: "var(--pd-panel)",
    border: "var(--pd-border)",
    borderRadius: "var(--pd-r-control)",
    boxShadow: "var(--pd-drop-sm)",
    cursor: "pointer",
  },
  burgerBar: {
    display: "block",
    height: 3,
    borderRadius: 2,
    background: "var(--pd-black)",
  },
  drawer: {
    position: "absolute",
    top: "100%",
    left: 14,
    right: 14,
    zIndex: 400,
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 12,
    background: "var(--pd-panel)",
    border: "var(--pd-border)",
    borderRadius: "var(--pd-r-panel)",
    boxShadow: "var(--pd-drop)",
  },
  drawerItem: {
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    background: "transparent",
    color: "var(--pd-ink-2)",
    border: "2px solid transparent",
    borderRadius: "var(--pd-r-control)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  drawerItemActive: {
    background: "var(--pd-slot)",
    color: "var(--pd-ink)",
    border: "var(--pd-border-sm)",
    boxShadow: "var(--pd-drop-sm)",
    fontWeight: 800,
  },
  drawerRule: {
    height: 2,
    background: "var(--pd-black)",
    opacity: 0.15,
    margin: "2px 0",
  },
  secondary: {
    padding: "9px 14px",
    background: "var(--pd-panel)",
    color: "var(--pd-ink)",
    border: "var(--pd-border-sm)",
    borderRadius: "var(--pd-r-control)",
    boxShadow: "var(--pd-drop-sm)",
    fontSize: 13.5,
    fontWeight: 800,
    cursor: "pointer",
  },
  primary: {
    padding: "9px 16px",
    background: "var(--pd-red)",
    color: "#ffffff",
    border: "var(--pd-border-sm)",
    borderRadius: "var(--pd-r-control)",
    boxShadow: "var(--pd-drop-sm)",
    fontSize: 13.5,
    fontWeight: 800,
    cursor: "pointer",
  },
};

export default CockpitHeader;
